import React, { useState, useRef, useEffect } from 'react';
import { X, Check, Info, AlertTri } from './Icons';

export function parseErr(e, fb = 'Something went wrong.') {
  const d = e?.response?.data?.detail;
  if (!d) return fb;
  if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join(', ');
  if (typeof d === 'string') return d;
  return fb;
}

export function Modal({ open, onClose, title, sm, children, footer }) {
  if (!open) return null;
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className={`modal${sm ? ' modal-sm' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="m-hd">
          <span className="m-title">{title}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: '4px' }}><X s={16}/></button>
        </div>
        <div className="m-body">{children}</div>
        {footer && <div className="m-ft">{footer}</div>}
      </div>
    </div>
  );
}

const BM = {
  active:'b-ok', inactive:'b-gray', open:'b-err', in_progress:'b-warn',
  resolved:'b-ok', closed:'b-gray', pending:'b-warn', completed:'b-ok',
  cancelled:'b-gray', contacted:'b-info', converted:'b-ok',
  paid:'b-ok', partial:'b-warn', credit:'b-warn',
  normal:'b-gray', high:'b-warn', critical:'b-err',
  superadmin:'b-pur', admin:'b-warn', field:'b-info',
  Inverter:'b-info', Battery:'b-ok', UPS:'b-warn',
  'Solar Inverter':'b-pur', 'Solar Panel':'b-info', Accessory:'b-gray',
};

export function Badge({ label, cls }) {
  return <span className={`badge ${cls || BM[label] || 'b-gray'}`}>{(label || '—').replace('_', ' ')}</span>;
}

export function Notice({ type = 'info', children }) {
  const cls = { info: 'n-info', ok: 'n-ok', success: 'n-ok', warn: 'n-warn', error: 'n-err' }[type] || 'n-info';
  const Icon = { info: Info, ok: Check, success: Check, warn: AlertTri, error: AlertTri }[type] || Info;
  return <div className={`notice ${cls}`}><Icon s={15}/><div>{children}</div></div>;
}

export function Spin({ label }) {
  return <div className="spin-wrap"><div className="spin"/>{label && <span>{label}</span>}</div>;
}

export function Empty({ msg }) {
  return (
    <div className="empty">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>
      </svg>
      <p>{msg || 'No records found'}</p>
    </div>
  );
}

export function CID({ code }) { return <span className="cid">{code}</span>; }

export function Confirm({ open, onClose, onOk, msg }) {
  return (
    <Modal open={open} onClose={onClose} title="Confirm Delete" sm
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-danger" onClick={onOk}>Delete</button></>}
    >
      <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>{msg || 'This action cannot be undone.'}</p>
    </Modal>
  );
}

export function useFlash() {
  const [f, setF] = useState(null);
  const show = (msg, type = 'ok') => { setF({ msg, type }); setTimeout(() => setF(null), 3500); };
  const Flash = () => f ? <Notice type={f.type}>{f.msg}</Notice> : null;
  return { show, Flash };
}

export function F({ label, req, full, children }) {
  return (
    <div className={`fg${full ? ' fg-full' : ''}`}>
      <label>{label}{req && <span style={{ color: 'var(--red)', marginLeft: 2 }}>*</span>}</label>
      {children}
    </div>
  );
}

export function MultiSelect({ options = [], value = [], onChange, placeholder = 'Select staff…' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = id => onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
  const label = value.length === 0
    ? placeholder
    : options.filter(o => value.includes(o.value)).map(o => o.label).join(', ');

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => setOpen(o => !o)} style={{
        width: '100%', padding: '9px 12px', border: '1px solid var(--border)',
        borderRadius: 'var(--r)', background: 'var(--white)', cursor: 'pointer',
        fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: 'var(--shadow-sm)', minHeight: 38, gap: 8,
        color: value.length ? 'var(--text)' : 'var(--text2)',
      }}>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          style={{ flexShrink: 0, opacity: 0.5, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 600,
          background: 'var(--white)', border: '1px solid var(--border)',
          borderRadius: 9, boxShadow: '0 6px 24px rgba(0,0,0,.11)',
          maxHeight: 220, overflowY: 'auto',
        }}>
          {options.length === 0
            ? <div style={{ padding: '10px 13px', fontSize: 13, color: 'var(--text2)' }}>No staff available</div>
            : options.map(o => {
                const sel = value.includes(o.value);
                return (
                  <div key={o.value} onClick={() => toggle(o.value)} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 13px', cursor: 'pointer', fontSize: 13,
                    background: sel ? 'rgba(59,130,246,.07)' : 'transparent',
                  }}>
                    <span style={{
                      width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                      border: `2px solid ${sel ? 'var(--blue)' : 'var(--border2)'}`,
                      background: sel ? 'var(--blue)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {sel && <Check s={10} color="#fff"/>}
                    </span>
                    {o.label}
                  </div>
                );
              })
          }
        </div>
      )}
    </div>
  );
}
